import { HISTORY_ACTION_MODE, Node as CraftNode } from '@candulabs/craft';
import { css, FlexIcon } from 'canoe';
import React, { ReactElement, useEffect } from 'react';
import styled from 'styled-components';

import { FloatingLabel } from '@candu-ai/craft/ui';
import Node from 'craft/components/Node';
import { BuiltInComponents } from 'craft/constants';
import { useChildNodes, useEditor, useNodeContext, useNodesData } from 'craft/hooks';
import { useColumnOperations } from 'craft/hooks/useColumnOperations';
import { useGridColumnClassNames } from 'craft/hooks/useGridColumnClassNames';
import { CustomNodeData, Resolver, ToolboxComponentSections } from 'craft/types';
import { hasDescendants } from 'craft/utils';

import { columnsInsertNodeTreesAtIndex } from './mutations';
import { ColumnsSettings } from './toolbox';
import { useGrowSiblingsIntoDeletedSpace } from './useGrowSiblingsIntoDeletedSpace';
import { useHasFocusWithinColumnSet } from './useHasFocusWithinColumnSet';

const ColumnsNode = styled(({ hasFocus, hasBgColor, ...props }) => <Node {...props} />)`
  ${({ theme, hasFocus }) => css`
    position: relative;
    ${hasFocus &&
    css`
      outline: 2px dashed ${theme.colors.BrandPurple} !important;

      &:hover {
        outline: 2px dashed ${theme.colors.BrandPurpleL10} !important;
      }
    `}
  `}
`;

export const COLUMNS_DEFAULT_STYLES = {
  display: 'grid',
  width: '100%',
  rowGap: '10px',
  columnGap: '20px',
  padding: 8,
};

interface Props {
  children?: ReactElement | ReactElement[];
  custom?: CustomNodeData;
}

export const Columns: Resolver<Props> = ({ children }: Props) => {
  const { id } = useNodeContext();
  const childNodeIds = useChildNodes(id);
  const { actions: editorActions, query } = useEditor();

  const childrenData = useNodesData(childNodeIds);

  const gridColumnsTemplateClassNames = useGridColumnClassNames(id);

  const { setColumnSize } = useColumnOperations({ parentNodeId: id });
  const { hasFocus } = useHasFocusWithinColumnSet(id);
  const hasBgColor = query.node(id).get()?.data?.custom?.css?.backgroundColor;

  useGrowSiblingsIntoDeletedSpace(setColumnSize, childrenData);

  // If we have no descendants remove this node
  useEffect(() => {
    // We must check to see if we've already been deleted or craft will crash
    const nodeExists = !!query.node(id).get();
    const shouldRemove = nodeExists && !hasDescendants(children);

    if (shouldRemove) {
      editorActions.deleteNodes([id], HISTORY_ACTION_MODE.MERGE);
    }
  }, [childrenData, children]);

  return (
    <ColumnsNode
      hasFocus={hasFocus}
      hasBgColor={hasBgColor}
      data-cy="node-columns"
      className={gridColumnsTemplateClassNames}
    >
      {hasFocus && <FloatingLabel icon={FlexIcon} label="Columns" />}
      {children}
    </ColumnsNode>
  );
};

Columns.craft = {
  name: BuiltInComponents.Columns,
  displayName: 'Column Group',
  isCanvas: true,
  rules: {
    canMoveIn: (incomingNodes: CraftNode[], self) =>
      incomingNodes.every(
        (node) => node.data.name === BuiltInComponents.Column && node.data.parent === self.id,
      ),
  },
};

Columns.toolbox = {
  schema: {
    [ToolboxComponentSections.Size]: {
      hasOverflow: true,
    },
  },
  component: [ColumnsSettings],
};

Columns.mutations = {
  insertNodeTreesAtIndex: columnsInsertNodeTreesAtIndex,
};
